import { useState, useEffect } from 'react';
import './accept_list.css'; // CSS 파일 import
import { get_testing_accept_list } from '../../utils/restapi';
import TESTING_ACCEPT_STATUS from '../../constants/testing_accept';

const TestingPrintAcceptList = ({ testingPlaceObj, testingScheduleObj, onClose }) => {
  // 목록
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // 제목에서 숫자만 추출하는 함수
  // 제목에서 회차 숫자만 뽑는다 — 첫 숫자 그룹만 사용해 "제 {숫자}회" 로 출력(회 중복 방지).
  const extractNumber = (title) => {
    if (!title) return '';
    const m = title.match(/\d+/);
    return m ? m[0] : '';
  };

  // 고사실별로 데이터 그룹화하는 함수
  const groupByRoom = (items) => {
    const grouped = {};
    items.forEach(item => {
      const roomNo = item.room_no || '미배정';
      if (!grouped[roomNo]) {
        grouped[roomNo] = [];
      }
      grouped[roomNo].push(item);
    });
    return grouped;
  };

  // 고사실별 인원수에 맞춰 응시자 표의 폰트 크기·셀 높이를 계산한다.
  // .page 자체를 A4보다 살짝 여유 있는 높이(285mm, css)로 고정해뒀으므로, 그 안에서 제목 영역을 뺀
  // "표에 실제로 쓸 수 있는 높이"를 기준으로 삼아 인원수만큼 나눠 셀 높이를 정한다.
  // 폰트는 7~12pt 사이(가독성 상/하한)에서 인원수에 반비례해 정하고,
  // 남는 공간은 셀 패딩을 키워서 채운다 — 그래서 폰트가 12pt로 막혀도
  // 인원이 적으면 패딩이 커져 용지를 꽉 채우고, 인원이 많으면 패딩이 줄어 한 장에 들어간다.
  const PT_TO_PX = 4 / 3;      // 1pt = 1.333px (CSS 기준 96dpi)
  const MM_TO_PX = 96 / 25.4;  // 1mm = 3.78px (CSS 기준 96dpi)

  const PAGE_HEIGHT_PX = 285 * MM_TO_PX;      // .page height: 285mm 과 동일
  const PAGE_PADDING_PX = 20;                 // .page padding: 20px (상하 합 40px, box-sizing:border-box)
  const HEADER_HEIGHT_PX = 150;               // 제목(h1) + 상단 정보 표가 차지하는 대략적인 높이 (여유 있게 잡음)
  const SAFETY_MARGIN = 0.85;                 // 셀 테두리·줄간격 오차로 한 장을 넘기지 않도록 두는 여유분
  const TABLE_BUDGET_PX = (PAGE_HEIGHT_PX - PAGE_PADDING_PX * 2 - HEADER_HEIGHT_PX) * SAFETY_MARGIN; // 응시자 표에 쓸 수 있는 높이

  const BASE_ROWS = 30;
  const BASE_FONT_PT = 11;
  const MIN_FONT_PT = 7;     // 인원이 아주 많아도 이보다 작게는 줄이지 않는다(가독성 하한)
  const MAX_FONT_PT = 12;    // 인원이 아주 적어도 이보다 크게는 키우지 않는다(과도한 확대 방지)
  const LINE_HEIGHT = 1.25;
  const MIN_PADDING_PX = 2;
  const MAX_PADDING_PX = 45; // 인원이 아주 적을 때 셀 높이가 얼마나 커질 수 있는지의 상한

  const getCompactStyle = (rowCount) => {
    const count = Math.max(rowCount, 1);
    const rawFontPt = (BASE_FONT_PT * BASE_ROWS) / count;
    const fontPt = Math.min(MAX_FONT_PT, Math.max(MIN_FONT_PT, rawFontPt));

    const fontPx = fontPt * PT_TO_PX;
    const contentHeightPx = fontPx * LINE_HEIGHT;
    const targetRowHeightPx = TABLE_BUDGET_PX / count;
    const rawPaddingPx = (targetRowHeightPx - contentHeightPx) / 2;
    const paddingPx = Math.min(MAX_PADDING_PX, Math.max(MIN_PADDING_PX, rawPaddingPx));

    return {
      fontSize: `${fontPt.toFixed(1)}pt`,
      lineHeight: `${LINE_HEIGHT}`,
      padding: `${paddingPx.toFixed(1)}px 0`,
    };
  };

  // 프린트 함수 - 간단하게 window.print() 사용
  const handlePrint = () => {
    const htmls = document.querySelectorAll('#testing_print_accept_list .body');
    htmls.forEach(html => {
      // 화면에 보이는 다이얼로그는 그대로 두고, 인쇄용으로만 복제본을 조정한다.
      const clone = html.cloneNode(true);
      clone.querySelectorAll('.page').forEach((page) => {
        const table = page.querySelectorAll('table')[1]; // 두 번째 표 = 응시자 목록
        if (!table) return;
        const rowCount = table.querySelectorAll('tbody tr').length;
        const { fontSize, padding, lineHeight } = getCompactStyle(rowCount);
        table.style.fontSize = fontSize;
        table.style.lineHeight = lineHeight;
        table.querySelectorAll('th, td').forEach((cell) => {
          cell.style.padding = padding;
        });
      });

      const newWin = window.open('', 'Print-Window');
      newWin.document.open();
      newWin.document.write(`
        <html>
          <head>
            <title>Print</title>
            <style>
              @page { size: A4; margin: 0; }
              /* 인쇄 시 배경색(노란 헤더)·글자색이 누락되지 않도록 강제 (원본 PHP 와 동일) */
              * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              #testing_print_accept_list { font-family: Arial, sans-serif; }
              #testing_print_accept_list .body { margin: 0; padding: 0; width: 100%; }
              #testing_print_accept_list .page { width: calc(100% - 40px); height: 285mm; margin: 0 auto; padding: 20px; box-sizing: border-box; }
              #testing_print_accept_list .page h1 { text-align: center; font-size: 12pt; line-height: 26px; margin: 10px 0 10px 0; }
              #testing_print_accept_list .page h1 b { font-size: 18pt; line-height: 26px; }
              #testing_print_accept_list .page table:nth-of-type(1) { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
              #testing_print_accept_list .page table:nth-of-type(1) td { text-align: center; vertical-align: middle; padding: 4px 15px; white-space: nowrap; }
              #testing_print_accept_list .page table:nth-of-type(1) td:nth-child(1) { font-size: 14pt; font-weight: bold; }
              #testing_print_accept_list .page table:nth-of-type(1) td:nth-child(2) { color: blue; font-size: 16pt; font-weight: bold; white-space: nowrap; }
              #testing_print_accept_list .page table:nth-of-type(1) td:nth-child(2) b { color: red; font-size: 26px; }
              #testing_print_accept_list .page table:nth-of-type(1) td:nth-child(3) { font-size: 14pt; font-weight: bold; white-space: nowrap; }
              #testing_print_accept_list .page table:nth-of-type(1) td:nth-child(3) span { font-size: 11pt; }
              #testing_print_accept_list .page table:nth-of-type(2) { width: 100%; border-collapse: collapse; }
              #testing_print_accept_list .page table:nth-of-type(2) thead th { border: 1px solid #000; background: yellow; text-align: center; vertical-align: middle; font-weight: bold; }
              #testing_print_accept_list .page table:nth-of-type(2) tbody td { border: 1px solid #000; text-align: center; vertical-align: middle; }
              /* 인쇄 시 head, foot 숨김 */
              #testing_print_accept_list .head, #testing_print_accept_list .foot { display: none !important; }
              /* 인쇄 시 .page마다 분할 — 고사실 1개당 1페이지 (인원수에 맞춰 폰트를 압축해 한 장에 담는다) */
              #testing_print_accept_list .page { page-break-after: always; break-after: page; }
              #testing_print_accept_list .page:last-child { page-break-after: auto; break-after: auto; }
            </style>
          </head>
          <body>
            <div id="testing_print_accept_list">
              <div class="body">
                ${clone.innerHTML}
              </div>
            </div>
          </body>
        </html>
      `);
      newWin.document.close();
      newWin.print();
      setTimeout(() => { newWin.close(); }, 10);
    });
  };

  // 리스트 가져오기 — 한 번에 큰 rowsPerPage로 조회하면 일정 인원 이상에서
  // 누락되는 사례가 있어, 페이지 단위로 나눠 조회한 뒤 이어붙인다.
  const handleGetData = async () => {
    const PAGE_SIZE = 100;
    const MAX_PAGES = 50; // 안전장치: 최대 5,000명까지 (무한 루프 방지)

    const baseJson = {
      testing_place_id: testingPlaceObj?.testing_place_id || 0,
      testing_schedule_id: testingScheduleObj?.testing_schedule_id || 0,
      // 고사장은 여러 지역본부 학생이 함께 배정되므로, 지역본부(hcodes)로 제한하지 않고
      // 이 고사장·일정에 배정된 응시자를 전부 가져온다.
      isAllocation: 1,
      // 접수신청(join) 단계에서도 고사실/좌석이 먼저 배정되는 경우(단체·오프라인 접수 등)가 있어,
      // decided 이상이 아니라 취소/환불/삭제대기 등을 제외한 "유효한" 상태 전체(used)로 조회한다.
      status: TESTING_ACCEPT_STATUS.used.join(','),
      sort: 'a_room_no*1, a_seat_no*1, a_code*1',
      desc: 'asc',
      rowsPerPage: PAGE_SIZE,
    };

    try {
      let combined = [];
      for (let page = 1; page <= MAX_PAGES; page++) {
        const response = await get_testing_accept_list({ ...baseJson, page });
        const list = response.lists || [];
        combined = combined.concat(list);

        const totalPages = parseInt(response.totalPages, 10) || 1;
        if (list.length < PAGE_SIZE || page >= totalPages) break;
      }
      console.log(`handleGetData: fetched ${combined.length} rows`);
      setItems(combined);
    } catch (error) {
      console.error("Error fetching testing places:", error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 고사실별 그룹화된 데이터
  const roomGroups = groupByRoom(items);

  return (
    <div id="testing_print_accept_list">
      <div className="head">
        <h1>응시자명단</h1>
      </div>
      <div className="body">
        {loading ? (
          <div className="notice">로딩중...</div>
        ) : items.length === 0 ? (
          <div className="notice">고사실/좌석 배정이 완료되지 않았습니다.</div>
        ) : Object.entries(roomGroups).map(([roomNo, roomItems], roomIndex) => {
          const compact = getCompactStyle(roomItems.length);
          const cellStyle = { padding: compact.padding };
          return (
            <div key={roomNo} className="page">
              <h1>제 <b>{extractNumber(testingScheduleObj?.testingObj?.title)}</b>회 한국 실용글쓰기검정</h1>
              <table>
                <tbody>
                  <tr>
                    <td>
                      {testingPlaceObj?.name}
                    </td>
                    <td
                      dangerouslySetInnerHTML={{
                        __html: roomNo.replace(/(\d+)/, '<b>$1</b>')
                      }}
                    ></td>
                    <td>
                      응시자명단 <span style={{ fontSize: '12pt' }}>(감독용)</span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <table style={{ fontSize: compact.fontSize, lineHeight: compact.lineHeight }}>
                <thead>
                  <tr>
                    <th style={cellStyle}>번호</th>
                    <th style={cellStyle}>고사장</th>
                    <th style={cellStyle}>아이디</th>
                    <th style={cellStyle}>생년월일</th>
                    <th style={cellStyle}>성명</th>
                    <th style={cellStyle}>수험번호</th>
                    <th style={cellStyle}>비고</th>
                  </tr>
                </thead>
                <tbody>
                  {roomItems.map((item, index) => (
                    <tr key={index}>
                      <td style={cellStyle}>{index + 1}</td>
                      <td style={cellStyle}>{testingPlaceObj?.name}</td>
                      <td style={cellStyle}>{item.userObj?.id ? item.userObj.id.substring(0, 4) + '**' : '-'}</td>
                      <td style={cellStyle}>{item.personObj?.birth || '-'}</td>
                      <td style={cellStyle}>{item.personObj?.name || '-'}</td>
                      <td style={cellStyle}>{item.code || '-'}</td>
                      <td style={cellStyle}></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
      <div className="foot">
        <button onClick={handlePrint}>인쇄</button>
        <button onClick={() => onClose()}>닫기</button>
      </div>
    </div>
  );
};

export default TestingPrintAcceptList;